import moment from "moment-timezone";
import { Moment } from "moment";
import useBackendRequest from '../useBackendRequest';
import IPagedList from '../../../types/PagedList';

export interface IPackageListItem {
  id: string;
  createdTimestamp: string;
  name: string;
  fileId: string | null;
  state: string;
  subjectsCount: number;
  fieldsCompositionName: string;
}

interface IPackageListItemResponse {
  id: string;
  createdTimestamp: string;
  createdTimestampFormatted: string;
  name: string;
  fileId: string | null;
  state: string;
  subjectsCount: number;
  fieldsCompositionName: string;
}

enum PackageSettingType {
  Manual = 1,
  Automatic = 2
}

export interface IActivePackageCard {
  settingType: string;
  settingTypeValue: PackageSettingType;
  lastPackageVersionTimestamp: string;
  lastPackageVersionTimestampFormatted: string;
  lastPackageVersionSubjectsCount: number;
  packageVersionsCountByCurrentMonth: number;
  activeSubjectsCountByCurrentMonth: number;
}

interface IActivePackageCardResponse {
  settingType: string;
  settingTypeValue: PackageSettingType;
  lastPackageVersionTimestamp: string;
  lastPackageVersionSubjectsCount: number;
  packageVersionsCountByCurrentMonth: number;
  activeSubjectsCountByCurrentMonth: number;
}

export enum FieldsComposition {
  CompaniesAndPersonsWithPassport = 1,
  PersonsWithoutPassport = 2
}

export interface IScheduleListItem {
  id: string;
  date: string,
  disabled: boolean,
  fieldsComposition: FieldsComposition
}

interface IScheduleListItemResponse {
  id: string;
  date: string,
  disabled: boolean,
  fieldsComposition: FieldsComposition
}

interface IPackagesApi {
  getPackages: (page: number, pageSize: number) => Promise<IPagedList<IPackageListItem>>;
  getActivePackage: (fieldsComposition: FieldsComposition) => Promise<IActivePackageCard>;
  initManualSetting: (fieldsComposition: FieldsComposition) => Promise<void>;
  getPotentialActivePackageSubjectsCount: (fieldsComposition: FieldsComposition) => Promise<number>;
  getSchedule: (fieldsComposition: FieldsComposition, page: number, pageSize: number) => Promise<IPagedList<IScheduleListItem>>;
  disableSchedule(id: string): Promise<void>;
  enableSchedule(id: string): Promise<void>;
  createSchedule(fieldsComposition: FieldsComposition, date: Moment): Promise<void>
}

export default function usePackagesApi(): IPackagesApi {

  const { makeRequest } = useBackendRequest();

  function getPackages(page: number, pageSize: number): Promise<IPagedList<IPackageListItem>> {

    return makeRequest<IPagedList<IPackageListItemResponse>, IPagedList<IPackageListItem>>(
      `/api/packages?page=${page}&pageSize=${pageSize}`,
      'get',
      response => {
        let list = response.list.map<IPackageListItem>(value => {
          let date = moment(value.createdTimestamp);
          return {
            ...value,
            createdTimestampFormatted: date.tz("Europe/Moscow").format('DD.MM.YYYY HH:mm:ss:SSS Z')
          }
        })
        return {
          list: list,
          page: response.page,
          pageSize: response.pageSize,
          totalRowCount: response.totalRowCount
        }
      },
      undefined
    );
  }

  function getActivePackage(fieldsComposition: FieldsComposition): Promise<IActivePackageCard> {
    return makeRequest<IActivePackageCardResponse, IActivePackageCard>(
      `/api/packages/activePackage?fieldsComposition=${fieldsComposition}`,
      'get',
      response => {
        return {
          ...response,
          lastPackageVersionTimestampFormatted:
            response.lastPackageVersionTimestamp
              ? moment(response.lastPackageVersionTimestamp).tz("Europe/Moscow").format('DD.MM.YYYY HH:mm:ss:SSS Z')
              : "",
        }
      },
      undefined
    );
  }

  function initManualSetting(fieldsComposition: FieldsComposition) {
    return makeRequest<void, void>(
      `/api/packages/activePackage/initManualSetting`,
      'post',
      () => { },
      { fieldsComposition: fieldsComposition }
    );
  }

  function getPotentialActivePackageSubjectsCount(fieldsComposition: FieldsComposition): Promise<number> {
    return makeRequest<number, number>(
      `/api/packages/activePackage/potentialSubjectsCount?fieldsComposition=${fieldsComposition}`,
      'get',
      response => { return response; },
      undefined
    );
  }

  function getSchedule(fieldsComposition: FieldsComposition, page: number, pageSize: number): Promise<IPagedList<IScheduleListItem>> {
    return makeRequest<IPagedList<IScheduleListItemResponse>, IPagedList<IScheduleListItem>>(
      `/api/packages/schedule?fieldsComposition=${fieldsComposition}&page=${page}&pageSize=${pageSize}`,
      'get',
      response => {
        let list = response.list.map<IScheduleListItem>(value => {
          let date = moment(value.date);
          return {
            ...value,
            date: date.format('DD.MM.YYYY')
          }
        })
        return {
          list: list,
          page: response.page,
          pageSize: response.pageSize,
          totalRowCount: response.totalRowCount
        } 
      },
      undefined
    )
  }

  function disableSchedule(id: string): Promise<void> {
    return makeRequest<void, void>(
      `/api/packages/schedule/${id}/disable`,
      'post',
      () => { },
      undefined
    );
  }

  function enableSchedule(id: string): Promise<void> {
    return makeRequest<void, void>(
      `/api/packages/schedule/${id}/enable`,
      'post',
      () => { },
      undefined
    );
  }

  function createSchedule(fieldsComposition: FieldsComposition, date: Moment): Promise<void> {
    return makeRequest<void, void>(
      `/api/packages/schedule`,
      'post',
      () => { },
      {
        fieldsComposition,
        date: date.format('YYYY-MM-DD')
      }
    );
  }


  return {
    getPackages,
    getActivePackage,
    getPotentialActivePackageSubjectsCount,
    initManualSetting,
    getSchedule,
    disableSchedule,
    enableSchedule,
    createSchedule
  }
}